import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { FeatureFlag } from '@/constants/featureFlags';
import { ApplePayStripeForm } from './ApplePayStripeForm';
import { ApplePayAdyenForm } from './ApplePayAdyenForm';
import { NuveiApplePayForm } from './NuveiApplePayForm';
import { ChargebeeApplePayForm } from './ChargebeeApplePayForm';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { ApplePayTabFallback } from '../Wallets/ApplePayTabFallback/ApplePayTabFallback';
import { useApplePayTabFallbackGate } from '@/hooks/useApplePayTabFallbackGate';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import { useExpressReveal } from '@/context/ExpressRevealContext';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { SubscriptionDisclaimer } from '../Subscriptions/SubscriptionDisclaimer/SubscriptionDisclaimer';

export const ApplePayForm = ({ isDeviceButton }: { isDeviceButton?: boolean }) => {
  const { paymentMethodsSettings, template, addressDetails, customer, featureFlags } = useContext(CheckoutContext);
  const useTabFallback = useApplePayTabFallbackGate();
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const [showZipCodeError, setShowZipCodeError] = useState(false);
  const providerSettings = paymentMethodsSettings.find(
    (paymentMethod) => paymentMethod.paymentMethodName === PaymentMethodEnum.APPLE_PAY
  )!;
  const shouldShowZipCodeErrorInInput = template.features.showBlockedZipCodeErrorInInput;
  const zipCode = addressDetails?.zipCode ?? customer.address?.zipCode;
  const handleInvalidTaxClick = useCallback(() => {
    if (shouldShowZipCodeErrorInInput) {
      setShowZipCodeError(true);
    }
  }, [shouldShowZipCodeErrorInInput]);

  const Component = useMemo(() => {
    if (providerSettings?.provider === ProviderEnum.STRIPE) {
      return (
        <ApplePayStripeForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    if (providerSettings?.provider === ProviderEnum.ADYEN) {
      return (
        <ApplePayAdyenForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    if (providerSettings?.provider === ProviderEnum.NUVEI) {
      // Soft kill switch: missing/undefined keeps wallets on; explicit false hides.
      if (featureFlags[FeatureFlag.NUVEI_WALLETS] === false) {
        return null;
      }
      return (
        <NuveiApplePayForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    // Subscriptions: Chargebee renders the sheet from its own iframe, so none of
    // the domain-verification workarounds below apply to it. The ZIP still has
    // to be collected on-page, hence the same `onInvalidTaxClick` wiring the
    // other providers get.
    if (providerSettings?.provider === ProviderEnum.CHARGEBEE) {
      return (
        <ChargebeeApplePayForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    return null;
  }, [providerSettings, handleInvalidTaxClick, shouldShowZipCodeErrorInInput, featureFlags]);

  const isChargebee = providerSettings?.provider === ProviderEnum.CHARGEBEE;

  // A host view (PREFERRED) can hide the WHOLE screen behind a skeleton until
  // Apple Pay confirms itself, and each provider form settles that handshake on
  // its own readiness path. Both branches below replace the provider form, so
  // they have to settle it themselves — otherwise the skeleton never lifts.
  const reveal = useExpressReveal();
  const reportWalletAvailability = useExpressWalletFallback();

  const handleTabFallbackReady = useCallback(
    (isAvailable: boolean) => {
      if (isAvailable) reveal?.onWalletReady();
    },
    [reveal]
  );

  // No provider form for this Apple Pay configuration (unsupported provider, or
  // the NUVEI_WALLETS kill switch above): nothing will ever report readiness, so
  // report unavailable once and let the template degrade to its fallback.
  const hasProviderForm = Component !== null;
  const hasReportedMissingFormRef = useRef(false);
  useEffect(() => {
    if (hasProviderForm || hasReportedMissingFormRef.current) return;
    hasReportedMissingFormRef.current = true;
    reportWalletAvailability(false, {
      wallet: 'apple_pay',
      provider: providerSettings?.provider ?? 'unknown',
      reason: 'no_provider_form',
    });
  }, [hasProviderForm, providerSettings?.provider, reportWalletAvailability]);

  if (!Component) {
    return <PaymentMethodNotSupported />;
  }

  // The provider button can never render here — either an iOS <= 16
  // cross-origin iframe (no `ApplePaySession` before Safari 17) or Apple
  // already rejected this page's merchant domain. Swap the whole form for
  // the fake button that opens the top-level fallback tab — tax and ZIP are
  // collected there by the real provider form. The fake button is always
  // usable, so its mount report is what reveals a gated host view.
  if (Component && useTabFallback && !isChargebee) {
    return <ApplePayTabFallback onReady={handleTabFallbackReady} />;
  }

  // ACDEV-25067 + ACDEV-27914: wallet sheet is the sole input surface for the
  // buyer's postal code — either because `WALLET_ZIP_TAX` is ON (25067) or
  // because the session started without a resolved US/CA tax (27914).
  // Bypass the on-page `InputTaxComponent`; the wallet-tax variants under
  // this path handle validation inside the sheet. Nuvei and Chargebee have no
  // in-sheet ZIP path yet, so they keep the on-page gate.
  if (isWalletZipTaxActive && providerSettings?.provider !== ProviderEnum.NUVEI && !isChargebee) return Component;

  return (
    <div className="flex flex-col gap-4">
      {!isDeviceButton && (
        <InputTaxComponent
          isBlockedForZipCode={showZipCodeError}
          onValidate={(isValid) => {
            if (isValid && zipCode?.trim()) setShowZipCodeError(false);
          }}
        />
      )}
      {/* Subscriptions only (renders null otherwise): ROSCA/FTC renewal
          disclosure directly above the wallet button — see InitialCreditCardForm
          for the card equivalent. */}
      <SubscriptionDisclaimer />
      {Component}
    </div>
  );
};
